import { FormEvent, useEffect, useState } from "react";
import { ApiError } from "../../api/client";
import {
  GearType,
  Vehicle,
  VehicleInput,
  createVehicle,
  setVehicleActive,
  updateVehicle,
} from "../../api/vehicles";
import { formatPlateDisplay } from "../../utils/plate";
import "./VehicleFormModal.css";

type Props = {
  vehicle: Vehicle | null;
  onClose: () => void;
  onSaved: (vehicle: Vehicle) => void;
};

function emptyInput(): VehicleInput {
  return {
    plateNo: "",
    brandModel: "",
    gearType: null,
    currentKm: 0,
    lastServiceDate: "",
    nextServiceKm: null,
    nextServiceDate: "",
    insuranceExpiryDate: "",
    inspectionExpiryDate: "",
    isActive: true,
    notes: "",
  };
}

function fromVehicle(vehicle: Vehicle): VehicleInput {
  return {
    plateNo: vehicle.plate_no,
    brandModel: vehicle.brand_model || "",
    gearType: vehicle.gear_type,
    currentKm: vehicle.current_km,
    lastServiceDate: vehicle.last_service_date ? vehicle.last_service_date.slice(0, 10) : "",
    nextServiceKm: vehicle.next_service_km,
    nextServiceDate: vehicle.next_service_date ? vehicle.next_service_date.slice(0, 10) : "",
    insuranceExpiryDate: vehicle.insurance_expiry_date ? vehicle.insurance_expiry_date.slice(0, 10) : "",
    inspectionExpiryDate: vehicle.inspection_expiry_date ? vehicle.inspection_expiry_date.slice(0, 10) : "",
    isActive: vehicle.is_active,
    notes: vehicle.notes || "",
  };
}

function cleanForApi(input: VehicleInput, isEdit: boolean): VehicleInput {
  const trim = (v: string | null | undefined) => (typeof v === "string" ? v.trim() : v);
  const optional = (v: string | null | undefined) => {
    const trimmed = trim(v);
    return trimmed && trimmed.length > 0 ? trimmed : null;
  };
  const result: VehicleInput = {
    plateNo: ((trim(input.plateNo) || "") as string).toUpperCase().replace(/\s+/g, ""),
    brandModel: optional(input.brandModel),
    gearType: input.gearType || null,
    lastServiceDate: optional(input.lastServiceDate),
    nextServiceKm:
      input.nextServiceKm === null || input.nextServiceKm === undefined || (input.nextServiceKm as unknown) === ""
        ? null
        : Number(input.nextServiceKm),
    nextServiceDate: optional(input.nextServiceDate),
    insuranceExpiryDate: optional(input.insuranceExpiryDate),
    inspectionExpiryDate: optional(input.inspectionExpiryDate),
    notes: optional(input.notes),
  };
  if (!isEdit) {
    result.currentKm =
      input.currentKm === undefined || (input.currentKm as unknown) === "" ? 0 : Number(input.currentKm);
    result.isActive = input.isActive ?? true;
  }
  return result;
}

export function VehicleFormModal({ vehicle, onClose, onSaved }: Props) {
  const [form, setForm] = useState<VehicleInput>(() =>
    vehicle ? fromVehicle(vehicle) : emptyInput(),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(vehicle ? fromVehicle(vehicle) : emptyInput());
    setError(null);
  }, [vehicle]);

  const isEdit = Boolean(vehicle);

  const update = <K extends keyof VehicleInput>(key: K, value: VehicleInput[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    const payload = cleanForApi(form, isEdit);
    if (!payload.plateNo) {
      setError("Plaka Zorunludur.");
      return;
    }
    setSubmitting(true);
    try {
      let saved: Vehicle;
      if (isEdit) {
        saved = await updateVehicle(vehicle!.id, payload);
        if (form.isActive !== undefined && form.isActive !== vehicle!.is_active) {
          saved = await setVehicleActive(vehicle!.id, form.isActive);
        }
      } else {
        saved = await createVehicle(payload);
      }
      onSaved(saved);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "Kayit Başarısız.");
      } else {
        setError("Sunucuya ulasilamadi.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="kp-modal-backdrop" role="dialog" aria-modal="true">
      <div className="kp-modal kp-card kp-vehicle-form">
        <div className="kp-modal__head">
          <div>
            <strong>{isEdit ? "Araç Düzenle" : "Yeni Arac"}</strong>
            <span>{isEdit ? formatPlateDisplay(vehicle!.plate_no) : "Yeni kayit olusturun"}</span>
          </div>
          <button className="kp-btn kp-btn--ghost" onClick={onClose} disabled={submitting}>
            Kapat
          </button>
        </div>

        <form className="kp-vehicle-form__grid" onSubmit={submit} autoComplete="off">
          <label className="kp-vehicle-form__field">
            <span>Plaka *</span>
            <input
              className="kp-input"
              type="text"
              required
              maxLength={20}
              value={form.plateNo}
              onChange={(e) => update("plateNo", e.target.value.toUpperCase().replace(/\s+/g, ""))}
              placeholder="34ABC123"
            />
          </label>

          <label className="kp-vehicle-form__field">
            <span>Marka / Model</span>
            <input
              className="kp-input"
              type="text"
              maxLength={150}
              value={form.brandModel || ""}
              onChange={(e) => update("brandModel", e.target.value)}
              placeholder="Renault Clio"
            />
          </label>

          <label className="kp-vehicle-form__field">
            <span>Vites Turu</span>
            <select
              className="kp-input"
              value={form.gearType || ""}
              onChange={(e) =>
                update("gearType", (e.target.value || null) as GearType | null)
              }
            >
              <option value="">-</option>
              <option value="manual">Manuel</option>
              <option value="automatic">Otomatik</option>
            </select>
          </label>

          {!isEdit && (
            <label className="kp-vehicle-form__field">
              <span>Mevcut KM</span>
              <input
                className="kp-input"
                type="number"
                min={0}
                value={form.currentKm ?? 0}
                onChange={(e) => update("currentKm", Number(e.target.value))}
              />
            </label>
          )}

          <label className="kp-vehicle-form__field">
            <span>En Son Bakım Tarihi</span>
            <input
              className="kp-input"
              type="date"
              value={form.lastServiceDate || ""}
              onChange={(e) => update("lastServiceDate", e.target.value)}
            />
          </label>

          <label className="kp-vehicle-form__field">
            <span>Sonraki Servis KM</span>
            <input
              className="kp-input"
              type="number"
              min={0}
              value={form.nextServiceKm === null || form.nextServiceKm === undefined ? "" : form.nextServiceKm}
              onChange={(e) =>
                update(
                  "nextServiceKm",
                  e.target.value === "" ? null : Number(e.target.value),
                )
              }
              placeholder="ornek: 25000"
            />
          </label>

          <label className="kp-vehicle-form__field">
            <span>Bir Sonraki Bakım Tarihi</span>
            <input
              className="kp-input"
              type="date"
              value={form.nextServiceDate || ""}
              onChange={(e) => update("nextServiceDate", e.target.value)}
            />
          </label>

          <label className="kp-vehicle-form__field">
            <span>Sigorta Bitis Tarihi</span>
            <input
              className="kp-input"
              type="date"
              value={form.insuranceExpiryDate || ""}
              onChange={(e) => update("insuranceExpiryDate", e.target.value)}
            />
          </label>

          <label className="kp-vehicle-form__field">
            <span>Muayene Bitis Tarihi</span>
            <input
              className="kp-input"
              type="date"
              value={form.inspectionExpiryDate || ""}
              onChange={(e) => update("inspectionExpiryDate", e.target.value)}
            />
          </label>

          <label className="kp-vehicle-form__field kp-vehicle-form__field--full">
            <span>Notlar</span>
            <textarea
              className="kp-input"
              rows={3}
              maxLength={2000}
              value={form.notes || ""}
              onChange={(e) => update("notes", e.target.value)}
            />
          </label>

          {isEdit && (
            <div className="kp-vehicle-form__field kp-vehicle-form__field--full kp-form-status">
              <span>Durum</span>
              <label className="kp-form-status__toggle">
                <input
                  type="checkbox"
                  checked={form.isActive !== false}
                  onChange={(e) => update("isActive", e.target.checked)}
                />
                <span>{form.isActive !== false ? "Aktif arac" : "Pasif arac"}</span>
              </label>
              <small className="kp-form-status__hint">
                Pasif araclar yeni randevu planlamada onerilmez; mevcut kayitlar korunur.
              </small>
            </div>
          )}

          {error && <div className="kp-vehicle-form__error">{error}</div>}

          <div className="kp-vehicle-form__actions">
            <button type="button" className="kp-btn" onClick={onClose} disabled={submitting}>
              İptal
            </button>
            <button type="submit" className="kp-btn kp-btn--primary" disabled={submitting}>
              {submitting ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Oluştur"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
